import { ComponentType } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { ExploreCatalogCard as ExploreCatalogCardType } from '../../core/types/explore.types'
import { resolveIconToken } from '../../core/utils/explore-catalog'
import BarChartIcon from '../icons/bar-chart-icon'
import CalendarIcon from '../icons/calendar-icon'
import DollarIcon from '../icons/dollar-icon'
import MapPinIcon from '../icons/map-pin-icon'
import PackageIcon from '../icons/package-icon'
import TrendUpIcon from '../icons/trend-up-icon'

type ExploreCatalogCardProps = {
  card: ExploreCatalogCardType
  cardIndex: number
  onSelect: (card: ExploreCatalogCardType) => void
}

const ICON_COMPONENTS: Record<string, ComponentType<{ className?: string }>> = {
  PackageIcon,
  DollarIcon,
  TrendUpIcon,
  CalendarIcon,
  BarChartIcon,
  MapPinIcon,
}

const useStyles = makeStyles((theme) => ({
  card: {
    alignSelf: 'flex-start',
    height: 164,
    width: 'calc(25% - 9px)',
    background: 'var(--ds-bg-surface, #fff)',
    border: '1px solid rgba(220,223,232,0.95)',
    borderRadius: 14,
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'box-shadow 260ms ease, transform 150ms linear, border-color 260ms ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(15,23,42,0.1)',
      transform: 'translateY(-2px)',
      borderColor: 'var(--color-coral-sender, #E8735A)',
      '& $iconHost': {
        background: 'rgba(232,115,90,0.07)',
      },
      '& $icon': {
        color: 'var(--color-coral-sender, #E8735A)',
      },
    },
    '@media (max-width:1200px)': {
      width: 'calc(33.333% - 8px)',
    },
    '@media (max-width:800px)': {
      width: 'calc(50% - 6px)',
    },
  },
  iconHost: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    borderRadius: 10,
    flexShrink: 0,
    transition: 'background 260ms ease',
  },
  icon: {
    width: 20,
    height: 20,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    transition: 'color 260ms ease',
  },
  textBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  title: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
    lineHeight: 1.3,
  },
  description: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    lineHeight: 1.45,
    margin: 0,
  },
}))

const ExploreCatalogCard = ({ card, cardIndex, onSelect }: ExploreCatalogCardProps) => {
  const classes = useStyles()

  const iconName = resolveIconToken(card.icon)
  const IconComponent = ICON_COMPONENTS[iconName] ?? PackageIcon

  return (
    <button
      type="button"
      className={classes.card}
      style={{ '--card-index': cardIndex } as React.CSSProperties}
      onClick={() => onSelect(card)}
    >
      <div className={classes.iconHost}>
        <IconComponent className={classes.icon} />
      </div>
      <div className={classes.textBlock}>
        <p className={classes.title}>{card.title}</p>
        <p className={classes.description}>{card.description}</p>
      </div>
    </button>
  )
}

export default ExploreCatalogCard
